import { useMemo, useState } from 'react';
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Scan, IssueCounts, CategoryIssueCounts } from '../../types';

interface ScanProgressChartProps {
  scans: Scan[];
  className?: string;
}

interface ChartDataPoint {
  date: string;
  timestamp: number;
  scanId: string;
  score: number;
  total: number;
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
}

type TabKey = 'all' | keyof CategoryIssueCounts;

interface Tab {
  key: TabKey;
  label: string;
}

const TABS: Tab[] = [
  { key: 'all', label: 'All Issues' },
  { key: 'accessibility', label: 'Accessibility' },
  { key: 'eaa', label: 'EAA' },
  { key: 'seo', label: 'SEO' },
  { key: 'geo', label: 'GEO' },
  { key: 'brokenLinks', label: 'Broken Links' },
  { key: 'toxicLinks', label: 'Toxic Links' },
];

// Display window for the trend chart — purely a view filter, no data is removed.
type WindowKey = 'last10' | '30d' | '90d' | 'all';

interface WindowOption {
  key: WindowKey;
  label: string;
}

const WINDOWS: WindowOption[] = [
  { key: 'last10', label: 'Last 10' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'all', label: 'All' },
];

const DEFAULT_WINDOW: WindowKey = '90d';
const DAY_MS = 24 * 60 * 60 * 1000;

// Slice already-sorted (ascending) points to the selected window.
function applyWindow(points: ChartDataPoint[], window: WindowKey): ChartDataPoint[] {
  if (window === 'all') {
    return points;
  }
  if (window === 'last10') {
    return points.slice(-10);
  }
  const days = window === '30d' ? 30 : 90;
  const cutoff = Date.now() - days * DAY_MS;
  return points.filter((p) => p.timestamp >= cutoff);
}

// Severity colors from tailwind config
const COLORS = {
  critical: '#ef4444',
  serious: '#f97316',
  moderate: '#f59e0b',
  minor: '#6b7280',
  score: '#ff4d5a',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

function issueCountsToPoint(counts: IssueCounts) {
  return {
    critical: counts.critical,
    serious: counts.serious,
    moderate: counts.moderate,
    minor: counts.minor,
    total: counts.total,
  };
}

function transformScansToChartData(scans: Scan[], tab: TabKey): ChartDataPoint[] {
  return scans
    .filter((s) => s.status?.toLowerCase() === 'completed' && s.results)
    // Date the point by when the scan STARTED, matching the Scan History list
    // (ScanCard shows startedAt). Using completedAt made a scan that finished after
    // midnight land on the next day here while the list showed the previous day.
    .sort((a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime())
    .map((scan) => {
      const base = {
        date: formatDate(scan.startedAt),
        timestamp: new Date(scan.startedAt).getTime(),
        scanId: scan.scanId,
        score: scan.results!.overallScore,
      };

      if (tab === 'all') {
        return {
          ...base,
          ...issueCountsToPoint(scan.results!.uniqueIssues ?? scan.results!.totalIssues),
        };
      }

      const categoryCounts =
        scan.results!.uniqueCategoryIssueCounts?.[tab] ?? scan.results!.categoryIssueCounts?.[tab];
      if (categoryCounts) {
        return { ...base, ...issueCountsToPoint(categoryCounts) };
      }

      // Category data not available (old scan) — show zeros
      return { ...base, critical: 0, serious: 0, moderate: 0, minor: 0, total: 0 };
    });
}

// Custom tooltip component
function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const scoreEntry = payload.find((p) => p.dataKey === 'score');
  const score = scoreEntry?.value ?? 0;

  const critical = payload.find((p) => p.dataKey === 'critical')?.value ?? 0;
  const serious = payload.find((p) => p.dataKey === 'serious')?.value ?? 0;
  const moderate = payload.find((p) => p.dataKey === 'moderate')?.value ?? 0;
  const minor = payload.find((p) => p.dataKey === 'minor')?.value ?? 0;
  const total = Number(critical) + Number(serious) + Number(moderate) + Number(minor);

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-body-sm">
      <div className="font-semibold mb-2">{label}</div>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4">
          <span className="text-gray-600">Score</span>
          <span className="font-medium" style={{ color: COLORS.score }}>
            {score}
          </span>
        </div>
        <div className="border-t border-gray-100 pt-1 mt-1">
          <div className="flex items-center justify-between gap-4">
            <span className="text-gray-600">Total Issues</span>
            <span className="font-medium">{total}</span>
          </div>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.critical }} />
            <span className="text-gray-600">Critical</span>
          </div>
          <span>{critical}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.serious }} />
            <span className="text-gray-600">Serious</span>
          </div>
          <span>{serious}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.moderate }} />
            <span className="text-gray-600">Moderate</span>
          </div>
          <span>{moderate}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.minor }} />
            <span className="text-gray-600">Minor</span>
          </div>
          <span>{minor}</span>
        </div>
      </div>
    </div>
  );
}

export function ScanProgressChart({ scans, className = '' }: ScanProgressChartProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [activeWindow, setActiveWindow] = useState<WindowKey>(DEFAULT_WINDOW);

  // Full history (sorted ascending); the window selector slices a recent view of this.
  const allPoints = useMemo(() => transformScansToChartData(scans, activeTab), [scans, activeTab]);
  const chartData = useMemo(() => applyWindow(allPoints, activeWindow), [allPoints, activeWindow]);

  // Hide the card entirely only when there's not enough history to trend at all.
  // A narrow window with too few points keeps the card (and selector) so the user can widen it.
  if (allPoints.length < 2) {
    return null;
  }

  const maxIssues = chartData.length > 0 ? Math.max(...chartData.map((d) => d.total)) : 0;
  const yAxisMax = Math.ceil(Math.max(maxIssues * 1.1, 1));
  const visibleTabs = TABS;

  return (
    <div className={`card ${className}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-body font-semibold">Issue Trends</h3>
        <div className="flex flex-wrap gap-1">
          {WINDOWS.map((win) => (
            <button
              key={win.key}
              onClick={() => setActiveWindow(win.key)}
              className={`px-2.5 py-1 rounded text-caption transition-colors ${
                activeWindow === win.key
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {win.label}
            </button>
          ))}
        </div>
      </div>
      {visibleTabs.length > 1 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-2.5 py-1 rounded text-caption transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}
      {chartData.length < 2 ? (
        <div
          className="w-full flex items-center justify-center text-body-sm text-gray-500"
          style={{ height: 300 }}
        >
          Not enough scans in this window — try a wider range.
        </div>
      ) : (
        <div
          className="w-full"
          style={{ height: 300 }}
          role="img"
          aria-label={`Chart showing ${TABS.find((t) => t.key === activeTab)?.label ?? 'issue'} trends over ${chartData.length} scans`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                stroke="#e5e7eb"
                tickLine={false}
              />
              <YAxis
                yAxisId="issues"
                orientation="left"
                tick={{ fontSize: 12, fill: '#6b7280' }}
                stroke="#e5e7eb"
                tickLine={false}
                axisLine={false}
                domain={[0, yAxisMax]}
                label={{
                  value: 'Issues',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12, fill: '#6b7280' },
                }}
              />
              <YAxis
                yAxisId="score"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                stroke="#e5e7eb"
                tickLine={false}
                axisLine={false}
                label={{
                  value: 'Score',
                  angle: 90,
                  position: 'insideRight',
                  style: { fontSize: 12, fill: '#6b7280' },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
                iconType="circle"
                iconSize={8}
              />

              {/* Stacked areas for severity breakdown */}
              <Area
                yAxisId="issues"
                type="monotone"
                dataKey="critical"
                stackId="1"
                fill={COLORS.critical}
                stroke={COLORS.critical}
                fillOpacity={0.8}
                name="Critical"
              />
              <Area
                yAxisId="issues"
                type="monotone"
                dataKey="serious"
                stackId="1"
                fill={COLORS.serious}
                stroke={COLORS.serious}
                fillOpacity={0.8}
                name="Serious"
              />
              <Area
                yAxisId="issues"
                type="monotone"
                dataKey="moderate"
                stackId="1"
                fill={COLORS.moderate}
                stroke={COLORS.moderate}
                fillOpacity={0.8}
                name="Moderate"
              />
              <Area
                yAxisId="issues"
                type="monotone"
                dataKey="minor"
                stackId="1"
                fill={COLORS.minor}
                stroke={COLORS.minor}
                fillOpacity={0.8}
                name="Minor"
              />

              {/* Score line overlay */}
              <Line
                yAxisId="score"
                type="monotone"
                dataKey="score"
                stroke={COLORS.score}
                strokeWidth={2}
                dot={{ r: 4, fill: COLORS.score }}
                activeDot={{ r: 6 }}
                name="Score"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
